import { switchString } from "src/utils/utils";
import receipt from "src/features/fields/assets/receipt.svg";
import pdf from "src/features/fields/assets/pdf.svg";
import download from "src/features/fields/assets/download.svg";
import check from "src/features/fields/assets/check.svg";
import { Button, Modal, Toggle } from "src/components";
import { useEffect, useState } from "react";
import EditStockistName from "./OrderInfoTabCards/EditStockist";
import CashDiscountChange from "./OrderInfoTabCards/CashDiscountChange";
import { RootState } from "src/context/store";
import { useToast } from "src/hooks/useToast";
import { useSelector } from "react-redux";
import { downloadAttachment, getAttachmentProofs, getCounterNames, sendMailToStockist, updateCashDiscountData } from "src/services/order-api";
import StockistSaoConfirmation from "./OrderInfoTabCards/StockistSaoConfirmation";
import ChangeOrderType from "./OrderInfoTabCards/ChangeOrderType";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import ChangeCounterLocation from "./OrderInfoTabCards/ChangeCounterLocation";
import { ordActions } from "src/context/order-slice";
import { useDispatch } from "react-redux";
import FormSuccess from "../../container/FormSuccess";
import EInvoiceToggleRemarks from "./OrderInfoTabCards/EInvoiceToggleRemarks";
import EwayToggleRemarks from "./OrderInfoTabCards/EwayToggleRemarks";
import SaoToggleRemarks from "./OrderInfoTabCards/SAOToggleRemarks";
import { einvTypeMap } from "src/pages/invoice-master/invoice-helper";// M10-12465
import { counterActions } from "src/context/counter-slice"; // M10-13417
import { Copy } from "lucide-react";
import { getOrderType } from "src/services/division-api";

interface OrderInfoHomeScreenInterface {
  orderInfo: any;
}

export const OrderInfoHomeScreen = ({ orderInfo }: OrderInfoHomeScreenInterface) => {
  const [editingCounterLocation, setEditingCounterLocation] = useState(false);
  const [editingStockistName, setEditingStockistName] = useState(false);
  const [editingCashDiscount, setEditingCashDiscount] = useState(false);
  const [editingOrderType, setEditingOrderType] = useState(false);
  const { orderDetailsFiltered, selectedCounterData } = useSelector((state: RootState) => state.ord);
  const { toast } = useToast();
  const [emailSent, setEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cashDiscountData, setCashDiscountData] = useState<any>(null);
  const [cashDiscountApplicable, setCashDiscountApplicable] = useState(false);
  const [attachmentProofs, setAttachmentProofs] = useState<{
    [key: string]: { verified: boolean; attachmentPath: string };
  }>({
    "Counter name": { verified: false, attachmentPath: "" },
    "Counter location": { verified: false, attachmentPath: "" },
    "Clinic name": { verified: false, attachmentPath: "" },
  });
  const orderDetails = orderDetailsFiltered?.[0];
  const [eInvoiceApplicable, setEInvoiceApplicable] = useState(orderDetails?.einv_exempt);
  const [eWayBillApplicable, setEWayBillApplicable] = useState(orderDetails?.eway_exempt);
  const [saoApplicable, setSaoApplicable] = useState(orderDetails?.sao_exempt);
  const [ewayRemarksOpen, setEwayRemarksOpen] = useState(false);
  const [einvRemarksOpen, setEinvRemarksOpen] = useState(false);
  const [saoRemarksOpen, setSaoRemarksOpen] = useState(false);
  const [orderTypeShortName, setOrderTypeShortName] = useState<string>("");

  const dispatch = useDispatch();

  // Order type short name from the cndn_remarks master; shows "-" until loaded or if not found.
  const displayOrderInfo = orderTypeShortName ? { ...orderInfo, "Order Type": orderTypeShortName } : orderInfo;

  const copyToClipboard = async (value: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback for non-secure contexts.
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast({ description: "Copied to clipboard", variant: "default" });
    } catch {
      toast({ description: "Failed to copy", variant: "destructive" });
    }
  };

  // Patient details are captured only for PUC orders (module_code 3).
  const patientCopyKeys = ["Name", "Mobile Number", "Address"];
  const patientDetails = orderDetails?.novu_metadata?.patient_details;
  const patientInfo =
    Number(orderDetails?.module_code) === 3
      ? {
        Name: patientDetails?.name || "-",
        "Age/Gender": `${patientDetails?.age ?? "-"}/${patientDetails?.gender || "-"}`,
        "Mobile Number": patientDetails?.mobile_number || "-",
        Address: patientDetails?.address || "-",
      }
      : null;

  const sentMailToStockist = async (orderCode: string) => {
    const payload = {
      orderData: {
        counter_ff_div_lnk_id: orderDetails?.counter_ff_div_lnk_id,
        novu_metadata: orderDetails?.novu_metadata,
        company_code: orderDetails?.company_code,
        stockist_id: orderDetails?.novu_metadata?.stockistData?.stockist_id,
        company_stockist_code: orderDetails?.company_stockist_code,
        order_code: orderCode,
        billed_price: orderDetails?.billed_price,
        order_cash_discount_perc: orderDetails?.order_cash_discount_perc,
        cndn_remarks_code_int: orderDetails?.cndn_remarks_code_int,
        company_name: orderDetails?.company_name,
        division_name: orderDetails?.division_name,
        actor_name: orderDetails?.actor_name,
        actor_code: orderDetails?.actor_code,
        actor_type: orderDetails?.actor_type,
      },
      order_code: orderCode,
      orderProducts: orderDetails?.orderProducts,
      order_place_date: orderDetails?.order_complete_date,
      action: "stockist_resend_email",
      isCustomEvent: true,
    };

    setIsLoading(true);

    try {
      const response = await sendMailToStockist(payload);
      console.log("response", response);
      setEmailSent(true);
      toast({ description: "Email sent successfully", variant: "default" });
    } catch (error: any) {
      console.error("Failed to send mail to stockist", error);
      toast({ description: "Failed to send mail to stockist", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const openGoogleMaps = (latitude: number, longitude: number) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, "_blank");
  };

  const handleUpdateCashDiscount = async () => {
    try {
      const response = await updateCashDiscountData(orderDetails?.counter_company_location_id, orderDetails?.company_divisioncode);
      setCashDiscountData(response?.data?.data);
      if (!response?.data?.data?.cashdiscount_applicable) {
        setCashDiscountApplicable(true);
        return;
      }
      setEditingCashDiscount(true);
    } catch (error) {
      console.log(error);
      toast({ description: "Failed to fetch cash discount data", variant: "destructive" });
    }
  };

  const handleGetAttachmentProofs = async () => {
    try {
      const response = await getAttachmentProofs(selectedCounterData?.counter?.id);
      response?.data?.forEach((item: any) => {
        const key = item?.validation_rules_id?.binding_key;
        const label =
          key === "counter_name" ? "Counter name" : key === "counter_location" ? "Counter location" : key === "clinic_name" ? "Clinic name" : null;
        if (label) {
          setAttachmentProofs((prev: any) => ({
            ...prev,
            [label]: { verified: true, attachmentPath: item?.attachment_path },
          }));
        }
      });
    } catch (error) {
      console.log(error);
      toast({ description: "Failed to fetch attachment proofs", variant: "destructive" });
    }
  };

  const fetchCounterData = async (counter_company_location_id: number) => {
    setIsLoading(true);
    // const query = `?counter_company_location_id=${counter_company_location_id}&stockist=true`;

    try {
      const response = await getCounterNames(counter_company_location_id);
      // const response = await getCounterByEmployee(query);
      dispatch(ordActions.setSelectedCounterData(response?.data?.data));
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(ordActions.setProductsData([]));
    dispatch(ordActions.clearCartDataObj());

    if (!selectedCounterData || Object.keys(selectedCounterData)?.length === 0) {
      const counterCompanyLocationId =
        Number(window.sessionStorage.getItem("counterCompanyLocationId")) || Number(window.localStorage.getItem("counterCompanyLocationId"));
      if (counterCompanyLocationId) {
        fetchCounterData(counterCompanyLocationId);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCounterData?.counter?.id) {
      handleGetAttachmentProofs();
    }
  }, [selectedCounterData]);

  useEffect(() => {
    const divisionCode = orderDetails?.company_divisioncode;
    const cndnCode = orderDetails?.cndn_remarks_code_int;
    setOrderTypeShortName("");
    if (!divisionCode || cndnCode === undefined || cndnCode === null) return;

    let ignore = false;
    getOrderType(divisionCode)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
        const match = list.find(
          (item: any) => String(item?.cndn_remarks_code_int ?? item?.cndn_remarks_code) === String(cndnCode)
        );
        if (!ignore && match?.cndn_remarks_short_name) setOrderTypeShortName(match.cndn_remarks_short_name);
      })
      .catch((error) => console.log(error));

    return () => {
      ignore = true;
    };
  }, [orderDetails?.company_divisioncode, orderDetails?.cndn_remarks_code_int]);

  return (
    <div className="p-6 mt-10 border border-gray-300 rounded-xl">
      <div className="flex justify-between border-b-2 border-gray-200 pb-5">
        <h1 className="text-[#49484C]  font-semibold text-[1.5rem]">
          <img src={receipt} alt="receipt" className="inline mr-2 w-6 h-6" />
          Order Info
        </h1>
        {(orderDetailsFiltered?.[0]?.orderStatusHdr?.order_status_id === 0 ||
          orderDetailsFiltered?.[0]?.orderStatusHdr?.order_status_id === 2 ||
          orderDetailsFiltered?.[0]?.orderStatusHdr?.order_status_id === 3 ||
          orderDetailsFiltered?.[0]?.orderStatusHdr?.order_status_id === 18) && (
          <Button
            onClick={() => sentMailToStockist(orderDetails?.order_code)}
            className={`rounded-lg px-4 py-1  text-white ${emailSent ? "border-2 border-gray-300 text-gray-500 cursor-not-allowed" : "bg-[#8051FF]"}`}
            disabled={emailSent}
            loaderSize="small"
            isLoading={isLoading}
            module={fineGrainedPermissionsKeys.ORDER.SENDEMAIL.module}
            action={fineGrainedPermissionsKeys.ORDER.SENDEMAIL.action}
          >
            {emailSent ? (
              <span className="flex items-center gap-2">
                <img src={check} alt="check" className="w-4 h-4" />
                Email Sent
              </span>
            ) : (
              "Resend Email"
            )}
          </Button>
        )}
      </div>
      <p className="text-[#49484C] font-semibold mt-8 text-lg">Summary</p>
      <div>
        <div className={"grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 w-[100%] py-5 xl:grid-cols-3 gap-y-5 mb-5"}>
          {Object.entries(displayOrderInfo)?.map(
            ([key, value]) =>
              typeof value === "string" &&
              value !== "" && (
                <div key={key?.toString()} className={"grid grid-cols-2 text-sm xl:grid-cols-2"}>
                  <p className="text-gray-500 place-self-center text-[1rem] mr-auto whitespace-break-spaces leading-[0.02rem] not-italic">
                    {switchString(key)} :
                  </p>
                  <div className="mr-auto">
                    <p className="text-[#1B1A1F] text-[1rem] flex items-center gap-2 text-left mr-auto">
                      {typeof value === "string" && key !== "Location" && key !== "Counter code" ? (
                        value?.length > 20 ? (
                          <span className="inline-flex items-center gap-1">
                            {/* native hover tooltip shows the full value */}
                            <span title={value}>{value.slice(0, 20) + "..."}</span>
                            <button
                              type="button"
                              title="Copy full value"
                              onClick={() => copyToClipboard(value)}
                              className="text-gray-400 hover:text-[#586af5] transition-colors"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </span>
                        ) : (
                          value
                        )
                      ) : ""}
                      {attachmentProofs[key]?.verified && (
                        <a href={attachmentProofs[key].attachmentPath} target="_blank" rel="noopener noreferrer">
                          <img src={check} alt="check" className="w-6 h-6" />
                        </a>
                      )}
                      {key === "Location" && (
                        <Button
                          className="border border-gray-300 rounded-lg px-2 py-1"
                          onClick={() => openGoogleMaps(Number(value?.split(",")[0]), Number(value?.split(",")[1]))}
                        >
                          Open Map
                        </Button>
                      )}
                      {key === "Stockist name" && (
                        <Button
                          className="border border-gray-300 rounded-lg px-2 py-1"
                          onClick={() => setEditingStockistName(true)}
                          module={fineGrainedPermissionsKeys.ORDER.UPDATESTOCKIST.module}
                          action={fineGrainedPermissionsKeys.ORDER.UPDATESTOCKIST.action}
                        >
                          Edit
                        </Button>
                      )}
                      {key === "Cash Discount %" && (
                        <Button
                          // disabled={!orderDetailsFiltered?.[0]?.cashdiscount_applicable}
                          className={`border border-gray-300 rounded-lg px-2 py-1`}
                          onClick={handleUpdateCashDiscount}
                          module={fineGrainedPermissionsKeys.ORDER.UPDATECASHDISCOUNT.module}
                          action={fineGrainedPermissionsKeys.ORDER.UPDATECASHDISCOUNT.action}
                        >
                          Edit
                        </Button>
                      )}
                      {key === "Order Type" && (
                        <Button
                          className="border border-gray-300 rounded-lg px-2 py-1"
                          onClick={() => setEditingOrderType(true)}
                          module={fineGrainedPermissionsKeys.ORDER.UPDATEORDERTYPE.module}
                          action={fineGrainedPermissionsKeys.ORDER.UPDATEORDERTYPE.action}
                        >
                          Edit
                        </Button>
                      )}
                      {key === "Counter location" && (
                        <Button
                          className="border border-gray-300 rounded-lg px-2 py-1"
                          onClick={() => setEditingCounterLocation(true)}
                          module={fineGrainedPermissionsKeys.ORDER.UPDATECOUNTERLOCATION.module}
                          action={fineGrainedPermissionsKeys.ORDER.UPDATECOUNTERLOCATION.action}
                        >
                          Edit
                        </Button>
                      )}
                      {/* M10-13417 */}
                      {key == "Counter code" && (
                        <a
                          className="text-gray-900 hover:text-[#6b40d6]"
                          href={`/masters/counters/details/?counter_code=${value}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            dispatch(counterActions.setClickType("row"));
                            dispatch(counterActions.setSelectedCounterData({}));
                            window.localStorage.setItem("counterOrderId", value);
                          }}
                        >
                          {value}
                        </a>
                      )}
                      {/* M10-13417 */}
                    </p>
                  </div>
                </div>
              )
          )}
        </div>
      </div>
      {patientInfo && (
        <div>
          <p className="text-[#49484C] font-semibold mt-8 mb-4 text-lg relative pr-4 after:content-[''] after:absolute after:w-[calc(100%-10rem)] after:h-[1px] after:bg-gray-300 after:top-1/2 after:right-0">Patient Details</p>
          <div className={"grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 w-[100%] py-5 xl:grid-cols-3 gap-y-5 mb-5"}>
            {Object.entries(patientInfo).map(([key, value]) => (
              <div key={key} className={"grid grid-cols-2 text-sm xl:grid-cols-2"}>
                <p className="text-gray-500 text-[1rem] mr-auto not-italic">{key} :</p>
                <p className="text-[#1B1A1F] text-[1rem] text-left mr-auto break-words flex items-center gap-2">
                  {value}
                  {patientCopyKeys.includes(key) && value !== "-" && (
                    <button
                      type="button"
                      title={`Copy ${key}`}
                      onClick={() => copyToClipboard(value)}
                      className="text-gray-400 hover:text-[#586af5] transition-colors shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <p className="text-[#49484C] font-semibold mt-8 mb-4 text-lg relative pr-4 after:content-[''] after:absolute after:w-[calc(100%-10rem)] after:h-[1px] after:bg-gray-300 after:top-1/2 after:right-0">E-Invoice / E-way</p>
        <div className="flex flex-col gap-3">

          {/* -------- E-INVOICE ROW --------   // M10-12465*/}
          <div className="flex items-center gap-10">
            <p className="text-gray-500 flex items-center gap-2">
              E-Invoice Required:
              <strong>
                {orderDetails?.novu_metadata?.e_inv_required === "Y" ? "Yes" : "No"}
              </strong>
            </p>

            {orderDetails?.novu_metadata?.e_inv_required === "Y" && (
              <p className="text-gray-500 flex items-center gap-2">
                E-Invoice Exempt:
                <Toggle
                  checked={orderDetails?.einv_exempt}
                  //disabled={!orderDetails?.exempt_enabled}
                  module={fineGrainedPermissionsKeys.ORDER.EINVEXEMPT.module} //M10-15571
                  action={fineGrainedPermissionsKeys.ORDER.EINVEXEMPT.action} //M10-15571
                  onChange={(e: any) => {
                    if (!orderDetails?.exempt_enabled) {
                      toast({ description: orderDetails?.exempt_remarks, variant: "destructive" });
                      return
                    }
                    setEinvRemarksOpen(true);
                    setEInvoiceApplicable(e === true);
                  }}
                />
              </p>
            )}
            {orderDetails?.novu_metadata?.e_inv_required === "Y" && (
              <p className="text-gray-500 flex items-center gap-2">
                E-Inv Category:
                <strong>
                  {einvTypeMap[
                    orderDetails?.novu_metadata?.stockistData?.elnv_invoice
                  ] || "-"}
                </strong>
              </p>
            )}
          </div>

          {/* -------- E-WAY BILL ROW -------- */}
          <div className="flex items-center gap-10">
            <p className="text-gray-500 flex items-center gap-2">
              E-Way Required:
              <strong>
                {orderDetails?.novu_metadata?.e_way_required ? "Yes" : "No"}
              </strong>
            </p>

            {orderDetails?.novu_metadata?.e_way_required && (
              <p className="text-gray-500 flex items-center gap-2">
                E-Way Bill Exempt:
                <Toggle
                  checked={orderDetails?.eway_exempt}
                  // disabled={!orderDetails?.exempt_enabled}
                  module={fineGrainedPermissionsKeys.ORDER.EWAYEXEMPT.module} //M10-15571
                  action={fineGrainedPermissionsKeys.ORDER.EWAYEXEMPT.action} //M10-15571
                  onChange={(e: any) => {
                     if (!orderDetails?.exempt_enabled) {
                      toast({ description: orderDetails?.exempt_remarks, variant: "destructive" });
                      return
                    }
                    setEwayRemarksOpen(true);
                    setEWayBillApplicable(e === true);
                  }}
                />
              </p>
            )}
          </div>
        </div>
      </div>
      {orderDetails?.cndn_remarks_code_int === 2 && <div>
        <p className="text-[#49484C] font-semibold mt-8 text-lg relative pr-4 after:content-[''] after:absolute after:w-[calc(100%-10rem)] after:h-[1px] after:bg-gray-300 after:top-1/2 after:right-0">SAO Exemptions</p>
        <div className="flex items-center gap-10">
          <p className="text-gray-500 mt-2 flex items-center gap-2">SAO Exempt:
            <Toggle
              checked={orderDetails?.sao_exempt}
              onChange={(e: any) => {
                setSaoRemarksOpen(true);
                setSaoApplicable(e === true ? true : false);
              }} /></p>
        </div>
      </div>}
      <StockistSaoConfirmation />
      {/* // M10-12465 */}
      <p className="text-[#49484C] font-semibold mt-8 text-lg">Attachments</p>
      <p className="text-[#49484C] mt-6 font-normal text-[1rem] relative pr-4 after:content-[''] after:absolute after:w-[calc(100%-8rem)] after:h-[1px] after:bg-gray-300 after:top-1/2 after:right-0">
        Counter Name
      </p>
      <div className="flex gap-5 mt-2">
        {orderDetails?.counter_copy_path.trim() ? (
          <div className="w-80 h-16 rounded-lg border border-[#E4E4E6] flex items-center justify-between px-2">
            <div className="flex gap-2">
              <img src={pdf} alt="pdf" className="w-10 h-10" />
              <div className="flex flex-col justify-center">
                <p className="text-[#324054] font-normal text-[.9rem]">Counter Attachment</p>
                {/* <p className="text-[#71839B] font-normal text-[.8rem]">313 KB. 31 Aug, 2022</p> */}
              </div>
            </div>
            <Button
              onClick={() => {
                if (!orderDetails?.counter_copy_path.trim()) {
                  toast({ description: "Counter attachment not found", variant: "destructive" });
                  return;
                }

                toast({ title: "Downloading...", description: "Downloading counter attachment", variant: "default" });

                downloadAttachment(orderDetails?.counter_copy_path.trim()).then((res: any) => {
                  if (res?.data?.success && res?.data?.data) {
                    window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                  } else {
                    console.error("Failed to download the invoice.");
                  }
                });
              }}
            >
              <img src={download} alt="download" className="w-6 h-6 text-gray-500 cursor-pointer" />
            </Button>
          </div>
        ) : (
          <div className="w-80 h-16 rounded-lg border border-[#E4E4E6] flex items-center justify-center px-2 text-center text-gray-500 text-sm">
            No Counter Attachment
          </div>
        )}
        {orderDetails?.stockist_copy_path.trim() ? (
          <div className="w-80 h-16 rounded-lg border border-[#E4E4E6] flex items-center justify-between px-2">
            <div className="flex gap-2">
              <img src={pdf} alt="pdf" className="w-10 h-10" />
              <div className="flex flex-col justify-center">
                <p className="text-[#324054] font-normal text-[.9rem]">Stockist Attachment</p>
                {/* <p className="text-[#71839B] font-normal text-[.8rem]">313 KB. 31 Aug, 2022</p> */}
              </div>
            </div>
            <Button
              onClick={() => {
                if (!orderDetails?.stockist_copy_path.trim()) {
                  toast({ description: "Stockist attachment not found", variant: "destructive" });
                  return;
                }

                toast({ title: "Downloading...", description: "Downloading stockist attachment", variant: "default" });

                downloadAttachment(orderDetails?.stockist_copy_path.trim()).then((res: any) => {
                  if (res?.data?.success && res?.data?.data) {
                    window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                  } else {
                    console.error("Failed to download the invoice.");
                  }
                });
              }}
            >
              <img src={download} alt="download" className="w-6 h-6 text-gray-500 cursor-pointer" />
            </Button>
          </div>
        ) : (
          <div className="w-80 h-16 rounded-lg border border-[#E4E4E6] flex items-center justify-center px-2 text-center text-gray-500 text-sm">
            No Stockist Attachment
          </div>
        )}
      </div>

      <Modal isOpen={editingStockistName} isHeader={false} onCloseModal={() => setEditingStockistName(false)}>
        <EditStockistName onCloseModal={() => setEditingStockistName(false)} />
      </Modal>
      <Modal isOpen={editingCashDiscount} isHeader={false} onCloseModal={() => setEditingCashDiscount(false)}>
        <CashDiscountChange cashDiscountData={cashDiscountData} onCloseModal={() => setEditingCashDiscount(false)} />
      </Modal>
      <Modal isOpen={editingOrderType} isHeader={false} onCloseModal={() => setEditingOrderType(false)}>
        <ChangeOrderType onCloseModal={() => setEditingOrderType(false)} />
      </Modal>
      <Modal isOpen={editingCounterLocation} isHeader={false} onCloseModal={() => setEditingCounterLocation(false)}>
        <ChangeCounterLocation onSuccess={() => {}} onCloseModal={() => setEditingCounterLocation(false)} />
      </Modal>
      <Modal
        isOpen={ewayRemarksOpen}
        isHeader={false}
        onCloseModal={() => {
          setEwayRemarksOpen(false);
          setEWayBillApplicable(orderDetails?.eway_exempt);
        }}
      >
        <EwayToggleRemarks
          eWayBillApplicable={eWayBillApplicable}
          onCloseModal={() => {
            setEwayRemarksOpen(false);
            setEWayBillApplicable(orderDetails?.eway_exempt);
          }}
        />
      </Modal>
      <Modal
        isOpen={einvRemarksOpen}
        isHeader={false}
        onCloseModal={() => {
          setEinvRemarksOpen(false);
          setEInvoiceApplicable(orderDetails?.einv_exempt);
        }}
      >
        <EInvoiceToggleRemarks
          eInvoiceApplicable={eInvoiceApplicable}
          onCloseModal={() => {
            setEinvRemarksOpen(false);
            setEInvoiceApplicable(orderDetails?.einv_exempt);
          }}
        />
      </Modal>

      <Modal isOpen={saoRemarksOpen} isHeader={false} onCloseModal={() => {
        setSaoRemarksOpen(false)
        setSaoApplicable(orderDetails?.sao_exempt)
      }}>
        <SaoToggleRemarks saoApplicable={saoApplicable} onCloseModal={() => {
          setSaoRemarksOpen(false)
          setSaoApplicable(orderDetails?.sao_exempt)
        }} />
      </Modal>

      <FormSuccess
        showForm={cashDiscountApplicable}
        onContinue={() => setCashDiscountApplicable(false)}
        onCloseModal={() => {}}
        variant="error"
        popupMessage={{
          description: "Cash discount is not applicable for this order",
          title: "Access Denied!",
        }}
      />
    </div>
  );
};
